import { Laptop, ShieldCheck, TriangleAlert } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { api, unwrap, type Schemas } from "@/api/client";
import {
  useDevices,
  useDisableMfa,
  useEnableMfa,
  useRevokeDevice,
  useStartMfaSetup,
  type Device,
} from "@/api/queries";
import { useAuth, useMe } from "@/app/auth";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { EmptyState } from "@/components/common/EmptyState";
import { CopyField } from "@/components/common/CopyField";
import { Field, PasswordInput } from "@/components/common/Field";
import { QrCode } from "@/components/common/QrCode";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { errorText, fieldError } from "@/lib/errors";
import { formatDuration, formatShortDateTime } from "@/lib/format";

import { PolicySummary } from "./PolicySummary";
import { Section } from "./SettingsLayout";

export function SecurityPage() {
  const { t } = useTranslation();
  const me = useMe();
  return (
    <Card className="px-6 py-6">
      <MfaSection />
      {!me.mfa_setup_required && <DevicesSection />}
      {!me.mfa_setup_required && (
        <Section title={t("settings.policy.mine.title")} description={t("settings.policy.mine.description")}>
          <PolicySummary userId={me.user.id} />
        </Section>
      )}
    </Card>
  );
}

function MfaSection() {
  const { t } = useTranslation();
  const me = useMe();
  const auth = useAuth();
  const start = useStartMfaSetup();
  const enable = useEnableMfa();
  const disable = useDisableMfa();
  const [setup, setSetup] = useState<Schemas["MfaSetup"] | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [justEnabled, setJustEnabled] = useState(false);
  const enabled = me.user.mfa_enabled;

  const refreshMe = async () => {
    auth.setMe(unwrap(await api.GET("/api/v1/me")));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!setup) return;
    try {
      await enable.mutateAsync({ setup_token: setup.setup_token, code: code.replace(/\s/g, ""), password });
      setSetup(null);
      setCode("");
      setPassword("");
      setJustEnabled(true);
      await refreshMe();
    } catch {
      // Shown below.
    }
  };

  return (
    <Section
      title={t("settings.mfa.title")}
      description={t("settings.mfa.description")}
      aside={
        <Badge variant={enabled ? "success" : "neutral"}>
          {enabled ? t("settings.mfa.on") : t("settings.mfa.off")}
        </Badge>
      }
    >
      <div className="flex flex-col gap-4">
        {me.mfa_setup_required && <Alert variant="warning">{t("settings.mfa.required")}</Alert>}
        {justEnabled && enabled && <Alert variant="success">{t("settings.mfa.enabled")}</Alert>}

        {enabled ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 font-medium text-success">
              <ShieldCheck className="size-5" aria-hidden="true" /> {t("settings.mfa.enabled")}
            </span>
            <Button variant="secondary" size="sm" onClick={() => setConfirmDisable(true)}>
              {t("settings.mfa.disable")}
            </Button>
          </div>
        ) : !setup ? (
          <div className="flex flex-col items-start gap-2">
            <Button
              disabled={start.isPending}
              onClick={() => void start.mutateAsync().then(setSetup, () => undefined)}
            >
              <ShieldCheck /> {t("settings.mfa.enable")}
            </Button>
            {start.error && <Alert variant="danger">{errorText(t, start.error)}</Alert>}
          </div>
        ) : (
          <form className="flex flex-col gap-5" onSubmit={(e) => void submit(e)}>
            <div className="flex flex-col gap-3">
              <p className="font-medium">{t("settings.mfa.step1")}</p>
              <div className="flex flex-wrap items-start gap-5">
                <QrCode value={setup.otpauth_url} label={t("settings.mfa.qrLabel")} />
                <div className="min-w-60 flex-1">
                  <p className="mb-2 text-sm text-muted">{t("settings.mfa.manual")}</p>
                  <CopyField label={t("settings.mfa.secret")} value={setup.secret} />
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <p className="font-medium">{t("settings.mfa.step2")}</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t("settings.mfa.code")} error={fieldError(t, enable.error, "code")}>
                  <Input
                    required
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={7}
                    className="font-mono tracking-[0.2em]"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ""))}
                  />
                </Field>
                <Field label={t("settings.mfa.password")} error={fieldError(t, enable.error, "password")}>
                  <PasswordInput
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Field>
              </div>
            </div>
            {enable.error &&
              !fieldError(t, enable.error, "code") &&
              !fieldError(t, enable.error, "password") && (
                <Alert variant="danger">{errorText(t, enable.error)}</Alert>
              )}
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={enable.isPending || code.replace(/\s/g, "").length !== 6 || !password}
              >
                {t("settings.mfa.confirm")}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setSetup(null)}>
                {t("common.cancel")}
              </Button>
            </div>
          </form>
        )}
      </div>

      {confirmDisable && (
        <ConfirmDialog
          danger
          title={t("settings.mfa.disableTitle")}
          description={t("settings.mfa.disableText")}
          confirmLabel={t("settings.mfa.disable")}
          errorText={(err) => errorText(t, err)}
          onConfirm={async () => {
            await disable.mutateAsync({ password: disablePassword });
            setJustEnabled(false);
            await refreshMe();
          }}
          onClose={() => {
            setConfirmDisable(false);
            setDisablePassword("");
          }}
        >
          <Field label={t("settings.mfa.password")}>
            <PasswordInput
              autoFocus
              autoComplete="current-password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
            />
          </Field>
        </ConfirmDialog>
      )}
    </Section>
  );
}

/** Warns when the computer's clock is off by more than a minute. */
function ClockOffset({ ms }: { ms?: number }) {
  const { t, i18n } = useTranslation();
  if (ms === undefined || Math.abs(ms) < 60_000) return null;
  // A positive offset means the server is ahead: the computer's clock is behind.
  const value = formatDuration(Math.abs(ms) / 1000, i18n.resolvedLanguage ?? "ru");
  return (
    <div
      className="mt-0.5 flex items-center gap-1 text-xs text-warning"
      title={t("settings.devices.clockHint")}
    >
      <TriangleAlert className="size-3.5" aria-hidden />
      {ms > 0 ? t("settings.devices.clockBehind", { value }) : t("settings.devices.clockAhead", { value })}
    </div>
  );
}

function DevicesSection() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const me = useMe();
  const devices = useDevices(me.user.id);
  const revoke = useRevokeDevice();
  const [target, setTarget] = useState<Device | null>(null);

  return (
    <Section title={t("settings.devices.title")} description={t("settings.devices.description")}>
      {devices.isPending ? (
        <Skeleton className="h-24" />
      ) : devices.isError ? (
        <Alert variant="danger">{errorText(t, devices.error)}</Alert>
      ) : devices.data.length === 0 ? (
        <Card className="shadow-none">
          <EmptyState icon={Laptop} title={t("settings.devices.empty")} className="py-8" />
        </Card>
      ) : (
        <Card className="overflow-hidden shadow-none">
          <Table className="min-w-[560px]">
            <THead>
              <tr>
                <TH>{t("settings.devices.name")}</TH>
                <TH>{t("settings.devices.version")}</TH>
                <TH>{t("settings.devices.lastSeen")}</TH>
                <TH>
                  <span className="sr-only">{t("common.actions")}</span>
                </TH>
              </tr>
            </THead>
            <tbody>
              {devices.data.map((d) => (
                <TR key={d.id}>
                  <TD>
                    <div className="font-medium">{d.name}</div>
                    <div className="text-xs text-muted">
                      {t(`os.${d.os}`)} {d.os_version}
                    </div>
                  </TD>
                  <TD className="tabular-nums">{d.agent_version || "—"}</TD>
                  <TD className="whitespace-nowrap text-muted tabular-nums">
                    {d.last_seen_at ? formatShortDateTime(d.last_seen_at, lang) : t("common.never")}
                    {!d.revoked && <ClockOffset ms={d.clock_offset_ms} />}
                  </TD>
                  <TD className="text-right">
                    {d.revoked ? (
                      <Badge>{t("settings.devices.revoked")}</Badge>
                    ) : (
                      <Button variant="secondary" size="sm" onClick={() => setTarget(d)}>
                        {t("settings.devices.revoke")}
                      </Button>
                    )}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
      {target && (
        <ConfirmDialog
          danger
          title={t("settings.devices.revokeTitle", { name: target.name })}
          description={t("settings.devices.revokeText")}
          confirmLabel={t("settings.devices.revoke")}
          errorText={(err) => errorText(t, err)}
          onConfirm={() => revoke.mutateAsync(target.id)}
          onClose={() => setTarget(null)}
        />
      )}
    </Section>
  );
}
